import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import { Search, X } from 'lucide-react';
import { type ReactNode, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Button, Input, Loading, Modal, SearchInput } from './ui';
import { get } from '../lib/api';
import { mt } from '../lib/format';

/** Resultados por pesquisa nas listas de escolha (a API aceita até 100). */
const PICK_PAGE = 50;

/**
 * Combobox com pesquisa no servidor. Mostra o valor escolhido como um
 * "cartão" com botão para limpar; ao escrever, lista resultados navegáveis
 * por teclado. Mostra até PICK_PAGE resultados e diz quantos há ao todo —
 * antes eram 8, sem aviso, e a lista parecia não ter os restantes registos.
 */
export function AsyncPicker<T extends { id: string }>({
  endpoint, value, onChange, placeholder, render, renderSelected, autoFocus, disabled, footer, id,
}: {
  endpoint: (q: string) => string;
  value: T | null;
  onChange: (v: T | null) => void;
  placeholder: string;
  render: (v: T) => ReactNode;
  renderSelected?: (v: T) => ReactNode;
  autoFocus?: boolean;
  disabled?: boolean;
  footer?: ReactNode;
  id?: string;
}) {
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const [sel, setSel] = useState(0);
  const [debounced, setDebounced] = useState('');
  const box = useRef<HTMLDivElement>(null);
  useEffect(() => { const t = setTimeout(() => setDebounced(q), 180); return () => clearTimeout(t); }, [q]);
  const res = useQuery({ queryKey: ['picker', endpoint(debounced)], enabled: open, queryFn: () => get<{ items: T[]; total?: number }>(endpoint(debounced)) });
  const items = res.data?.items ?? [];
  const more = (res.data?.total ?? 0) - items.length;
  useEffect(() => setSel(0), [debounced]);
  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => { if (!box.current?.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  if (value) {
    return (
      <div className="flex items-center gap-3 rounded-xl border border-[var(--border-strong)] bg-[var(--surface-2)] px-3 py-2.5">
        <div className="min-w-0 flex-1">{(renderSelected ?? render)(value)}</div>
        {!disabled && <button type="button" onClick={() => onChange(null)} className="rounded-md p-1 text-[var(--muted)] hover:bg-[var(--surface)] hover:text-[var(--text)]" aria-label="Limpar selecção"><X className="size-4" /></button>}
      </div>
    );
  }
  const pick = (v?: T) => { if (v) { onChange(v); setOpen(false); setQ(''); } };
  return (
    <div ref={box} className="relative">
      <div className="relative">
        <Search className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-[var(--muted)]" />
        <input id={id} autoFocus={autoFocus} disabled={disabled} value={q} placeholder={placeholder} role="combobox" aria-expanded={open} aria-autocomplete="list"
          onFocus={() => setOpen(true)} onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          onKeyDown={(e) => {
            if (e.key === 'ArrowDown') { e.preventDefault(); setSel((s) => Math.min(items.length - 1, s + 1)); }
            if (e.key === 'ArrowUp') { e.preventDefault(); setSel((s) => Math.max(0, s - 1)); }
            if (e.key === 'Enter') { e.preventDefault(); pick(items[sel]); }
            if (e.key === 'Escape') setOpen(false);
          }}
          className="h-10 w-full rounded-xl border border-[var(--border-strong)] bg-[var(--surface)] pr-3 pl-9 text-sm shadow-xs focus:border-brand-500 focus:ring-3 focus:ring-brand-500/15 focus:outline-none" />
      </div>
      {open && (
        <div className="surface absolute z-30 mt-1.5 w-full overflow-hidden rounded-xl shadow-xl shadow-black/10">
          <ul role="listbox" className="max-h-72 overflow-y-auto p-1">
            {res.isLoading && <li className="px-3 py-3 text-sm text-[var(--muted)]">A pesquisar…</li>}
            {!res.isLoading && items.length === 0 && <li className="px-3 py-3 text-sm text-[var(--muted)]">Sem resultados</li>}
            {items.map((it, i) => (
              <li key={it.id} ref={i === sel ? (el) => el?.scrollIntoView({ block: 'nearest' }) : undefined}>
                <button type="button" role="option" aria-selected={i === sel} onMouseEnter={() => setSel(i)} onMouseDown={(e) => { e.preventDefault(); pick(it); }}
                  className={clsx('w-full rounded-lg px-3 py-2 text-left text-sm', i === sel && 'bg-brand-500/10')}>{render(it)}</button>
              </li>
            ))}
          </ul>
          {more > 0 && <div className="border-t border-[var(--border)] px-3 py-2 text-xs text-[var(--muted)]">A mostrar {items.length} de {res.data?.total} — escreva para filtrar.</div>}
          {footer && <div className="border-t border-[var(--border)] p-1">{footer}</div>}
        </div>
      )}
    </div>
  );
}

export interface PickCustomer {
  id: string; name: string; nuit: string | null; code: string; is_final_consumer: boolean; city: string; address: string;
  withholding_pct: string | null; withholding_type: string | null; default_payment_method: string | null; default_discount_pct: string | null;
  billing_email: string | null; email: string | null; delivery_address: string | null; payment_terms_days: number | null;
}
export function CustomerPicker(p: { value: PickCustomer | null; onChange: (v: PickCustomer | null) => void; autoFocus?: boolean; disabled?: boolean; footer?: ReactNode; id?: string }) {
  return (
    <AsyncPicker<PickCustomer> {...p} placeholder="Pesquisar cliente por nome, NUIT ou código…"
      endpoint={(q) => `/customers?pageSize=${PICK_PAGE}&q=${encodeURIComponent(q)}`}
      render={(c) => (
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0"><div className="truncate font-medium">{c.name}</div><div className="truncate text-xs text-[var(--muted)]">{c.code}{c.city ? ` · ${c.city}` : ''}</div></div>
          <span className="shrink-0 text-xs text-[var(--muted)]">{c.nuit ? `NUIT ${c.nuit}` : c.is_final_consumer ? 'Consumidor final' : <span className="text-amber-600">sem NUIT</span>}</span>
        </div>
      )} />
  );
}

export interface PickProduct { id: string; code: string; name: string; unit: string; unit_price: string; price_includes_tax: boolean; tax_rate: string; exemption_code: string | null; avg_cost: string; cost_price: string; track_stock: boolean; stock?: string; description: string | null }
export function ProductPicker(p: { value: PickProduct | null; onChange: (v: PickProduct | null) => void; showCost?: boolean }) {
  return (
    <AsyncPicker<PickProduct> value={p.value} onChange={p.onChange} placeholder="Artigo…"
      endpoint={(q) => `/products?pageSize=${PICK_PAGE}&q=${encodeURIComponent(q)}`}
      render={(a) => (
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0"><div className="truncate font-medium">{a.name}</div><div className="text-xs text-[var(--muted)]">{a.code}{a.track_stock && a.stock !== undefined ? ` · stock ${Number(a.stock)}` : ''}</div></div>
          <span className="shrink-0 text-xs tabular-nums text-[var(--muted)]">{mt(p.showCost ? a.avg_cost : a.unit_price)}</span>
        </div>
      )} />
  );
}

export interface PickSupplier { id: string; code: string; name: string; nuit: string | null; city: string; payment_terms_days: number | null; default_payment_method: string | null }
export function SupplierPicker(p: { value: PickSupplier | null; onChange: (v: PickSupplier | null) => void; autoFocus?: boolean; disabled?: boolean; footer?: ReactNode }) {
  return (
    <AsyncPicker<PickSupplier> {...p} placeholder="Pesquisar fornecedor…"
      endpoint={(q) => `/suppliers?pageSize=${PICK_PAGE}&q=${encodeURIComponent(q)}`}
      render={(s) => (
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0"><div className="truncate font-medium">{s.name}</div><div className="text-xs text-[var(--muted)]">{s.code}{s.city ? ` · ${s.city}` : ''}</div></div>
          <span className="shrink-0 text-xs text-[var(--muted)]">{s.nuit ? `NUIT ${s.nuit}` : ''}</span>
        </div>
      )} />
  );
}

/** Lista de armazéns (cache partilhada). */
export function useWarehouses() {
  return useQuery({ queryKey: ['warehouses'], queryFn: () => get<{ items: { id: string; name: string; is_default: boolean; active: boolean; value: string }[] }>('/stock/warehouses'), staleTime: 60_000 });
}
export function useCategories() {
  return useQuery({ queryKey: ['categories'], queryFn: () => get<{ items: { id: string; name: string; products: number }[] }>('/stock/categories'), staleTime: 60_000 });
}
type Exemption = { code: string; description: string; legal_basis: string; requires_text: boolean; valid_to: string | null };
export function useReference() {
  return useQuery({
    queryKey: ['reference'], staleTime: Infinity,
    queryFn: () => get<{ exemptions: Exemption[]; expiredExemptions?: Exemption[]; paymentMethods: Record<string, string> }>('/reference'),
  });
}

/**
 * Campo de descrição de linha com sugestões de artigos: escreve-se texto
 * livre ou escolhe-se um artigo (preenche preço, IVA, unidade).
 *
 * A lista abre num PORTAL com posição fixa: dentro da tabela de linhas (que
 * tem deslocamento horizontal) uma lista absoluta ficava cortada e invisível —
 * era por isso que "não carregava artigos". Ao focar, mostra logo artigos,
 * sem ser preciso escrever.
 */
export function ProductSuggest({ value, onText, onPick, placeholder = 'Escreva ou escolha um artigo…', showCost, ariaLabel, onBrowse }: {
  value: string; onText: (v: string) => void; onPick: (p: PickProduct) => void; placeholder?: string; showCost?: boolean; ariaLabel?: string; onBrowse?: () => void;
}) {
  const [focus, setFocus] = useState(false);
  const [sel, setSel] = useState(0);
  const [debounced, setDebounced] = useState(value);
  const [rect, setRect] = useState<DOMRect | null>(null);
  const input = useRef<HTMLInputElement>(null);
  useEffect(() => { const t = setTimeout(() => setDebounced(value), 160); return () => clearTimeout(t); }, [value]);
  const q = useQuery({
    queryKey: ['products-suggest', debounced.trim()], enabled: focus,
    queryFn: () => get<{ items: PickProduct[]; total: number }>(`/products?pageSize=${PICK_PAGE}&q=${encodeURIComponent(debounced.trim())}`),
  });
  const items = focus ? q.data?.items ?? [] : [];
  useEffect(() => setSel(0), [debounced]);
  useEffect(() => {
    if (!focus) return;
    const place = () => input.current && setRect(input.current.getBoundingClientRect());
    place();
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    return () => { window.removeEventListener('scroll', place, true); window.removeEventListener('resize', place); };
  }, [focus]);
  const below = rect ? window.innerHeight - rect.bottom > 300 : true;
  const list = focus && rect && (items.length > 0 || q.isFetching || onBrowse) ? createPortal(
    <div className="surface fixed z-[70] overflow-hidden rounded-xl shadow-2xl shadow-black/15"
      style={{ left: rect.left, width: Math.max(rect.width, 420), ...(below ? { top: rect.bottom + 4 } : { bottom: window.innerHeight - rect.top + 4 }) }}>
      <ul role="listbox" className="max-h-72 overflow-auto p-1">
        {q.isFetching && !items.length && <li className="px-3 py-2.5 text-sm text-[var(--muted)]">A procurar artigos…</li>}
        {!q.isFetching && !items.length && <li className="px-3 py-2.5 text-sm text-[var(--muted)]">Nenhum artigo — fica como descrição livre.</li>}
        {items.map((p, i) => (
          <li key={p.id} ref={i === sel ? (el) => el?.scrollIntoView({ block: 'nearest' }) : undefined}>
            <button type="button" role="option" aria-selected={i === sel} onMouseEnter={() => setSel(i)} onMouseDown={(e) => { e.preventDefault(); onPick(p); setFocus(false); input.current?.blur(); }}
              className={clsx('flex w-full items-center justify-between gap-3 rounded-lg px-3 py-2 text-left text-sm', i === sel && 'bg-brand-500/10')}>
              <span className="min-w-0"><span className="block truncate font-medium">{p.name}</span><span className="text-xs text-[var(--muted)]">{p.code}{p.track_stock && p.stock !== undefined ? ` · stock ${Number(p.stock)}` : ''}</span></span>
              <span className="shrink-0 text-right text-xs tabular-nums"><b>{mt(showCost ? (Number(p.avg_cost) ? p.avg_cost : p.cost_price) : p.unit_price)}</b><span className="block text-[var(--muted)]">{Number(p.tax_rate) ? `IVA ${Number(p.tax_rate)}%${p.price_includes_tax ? ' incl.' : ''}` : 'Isento'}</span></span>
            </button>
          </li>
        ))}
      </ul>
      {(q.data?.total ?? 0) > items.length && <div className="border-t border-[var(--border)] px-3 py-2 text-xs text-[var(--muted)]">A mostrar {items.length} de {q.data?.total} artigos — escreva para filtrar.</div>}
      {onBrowse && <div className="border-t border-[var(--border)] p-1"><button type="button" onMouseDown={(e) => { e.preventDefault(); setFocus(false); onBrowse(); }} className="w-full rounded-lg px-3 py-2 text-left text-sm font-medium text-brand-700 hover:bg-brand-500/10 dark:text-brand-500">Abrir catálogo completo…</button></div>}
    </div>, document.body) : null;
  return (
    <>
      <input ref={input} value={value} placeholder={placeholder} aria-label={ariaLabel ?? placeholder} role="combobox" aria-expanded={items.length > 0} aria-autocomplete="list"
        onChange={(e) => { onText(e.target.value); setFocus(true); }} onFocus={() => setFocus(true)} onBlur={() => setTimeout(() => setFocus(false), 150)}
        onKeyDown={(e) => {
          if (e.key === 'Escape') { setFocus(false); return; }
          if (!items.length) return;
          if (e.key === 'ArrowDown') { e.preventDefault(); setSel((x) => Math.min(items.length - 1, x + 1)); }
          if (e.key === 'ArrowUp') { e.preventDefault(); setSel((x) => Math.max(0, x - 1)); }
          if (e.key === 'Enter') { e.preventDefault(); onPick(items[sel]!); setFocus(false); }
        }}
        className="h-9.5 w-full rounded-lg border border-[var(--border-strong)] bg-[var(--surface)] px-3 text-sm shadow-xs focus:border-brand-500 focus:ring-3 focus:ring-brand-500/15 focus:outline-none" />
      {list}
    </>
  );
}

/** Catálogo: pesquisar e adicionar vários artigos de uma vez, com quantidade. */
export function CatalogModal({ open, onClose, onAdd, showCost }: { open: boolean; onClose: () => void; onAdd: (items: { product: PickProduct; quantity: string }[]) => void; showCost?: boolean }) {
  const [q, setQ] = useState('');
  const [picked, setPicked] = useState<Record<string, { product: PickProduct; quantity: string }>>({});
  const [debounced, setDebounced] = useState('');
  useEffect(() => { const t = setTimeout(() => setDebounced(q), 180); return () => clearTimeout(t); }, [q]);
  useEffect(() => { if (open) { setPicked({}); setQ(''); } }, [open]);
  const r = useQuery({ queryKey: ['catalog', debounced], enabled: open, queryFn: () => get<{ items: PickProduct[]; total: number }>(`/products?pageSize=100&q=${encodeURIComponent(debounced)}`) });
  const n = Object.keys(picked).length;
  return (
    <Modal open={open} onClose={onClose} title="Catálogo de artigos" subtitle="Seleccione os artigos e as quantidades; são acrescentados como linhas." size="lg"
      footer={<><span className="mr-auto self-center text-sm text-[var(--muted)]">{n} seleccionado{n === 1 ? '' : 's'}</span><Button variant="secondary" onClick={onClose}>Cancelar</Button><Button disabled={!n} onClick={() => { onAdd(Object.values(picked)); onClose(); }}>Adicionar {n || ''} linha{n === 1 ? '' : 's'}</Button></>}>
      <div className="space-y-3">
        <SearchInput value={q} onChange={setQ} placeholder="Código, designação ou código de barras" />
        <div className="max-h-[50vh] overflow-y-auto rounded-xl border border-[var(--border)]">
          {r.isLoading ? <div className="p-4"><Loading rows={4} /></div> : !r.data?.items.length ? <p className="p-6 text-center text-sm text-[var(--muted)]">Sem artigos.</p> : (
            <table className="w-full text-sm">
              <tbody>{r.data.items.map((p) => {
                const on = !!picked[p.id];
                return (
                  <tr key={p.id} className={clsx('border-b border-[var(--border)] last:border-0', on && 'bg-brand-500/5')}>
                    <td className="w-10 py-2 pl-3"><input type="checkbox" aria-label={`Seleccionar ${p.name}`} className="size-4 accent-[var(--color-brand-700)]" checked={on}
                      onChange={(e) => setPicked((x) => { const c = { ...x }; if (e.target.checked) c[p.id] = { product: p, quantity: '1' }; else delete c[p.id]; return c; })} /></td>
                    <td className="py-2"><div className="font-medium">{p.name}</div><div className="text-xs text-[var(--muted)]">{p.code} · {p.unit}{p.track_stock && p.stock !== undefined ? ` · stock ${Number(p.stock)}` : ''}</div></td>
                    <td className="py-2 text-right tabular-nums">{mt(showCost ? (Number(p.avg_cost) ? p.avg_cost : p.cost_price) : p.unit_price)}<div className="text-xs text-[var(--muted)]">{Number(p.tax_rate) ? `IVA ${Number(p.tax_rate)}%` : 'Isento'}</div></td>
                    <td className="w-28 py-2 pr-3 pl-3">{on && <Input className="num h-8" inputMode="decimal" aria-label={`Quantidade de ${p.name}`} value={picked[p.id]!.quantity} onChange={(e) => setPicked((x) => ({ ...x, [p.id]: { ...x[p.id]!, quantity: e.target.value } }))} />}</td>
                  </tr>
                );
              })}</tbody>
            </table>
          )}
        </div>
        {r.data && r.data.total > r.data.items.length && <p className="text-xs text-[var(--muted)]">A mostrar {r.data.items.length} de {r.data.total} artigos — pesquise para encontrar os restantes.</p>}
      </div>
    </Modal>
  );
}
